/**
 * Structured @mention format — web mirror of the app-wide primitive
 * (apps/api/src/services/structuredMentions.js). A mention is
 * `@[Display Name](user:<uuid>)`. Pure, no React.
 *
 * Used by both the messenger composer/renderer and the PM issue-comment
 * composer/renderer, so the two surfaces speak one mention format. Keep the
 * regex in lockstep with the server's MENTION_RE.
 */

// uuid group requires canonical 8-4-4-4-12 shape (mirrors the server) so a
// malformed token renders as plain text, not a chip.
const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const MENTION_RE = new RegExp(`@\\[([^\\]]+)\\]\\(user:(${UUID})\\)`, 'g');

/**
 * @param {string} body
 * @returns {Array<{type:'text', value:string} | {type:'mention', display:string, userId:string}>}
 */
export function tokenizeMentions(body) {
  if (!body || typeof body !== 'string') return [];
  const out = [];
  let last = 0;
  let m;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(body)) !== null) {
    if (m.index > last) out.push({ type: 'text', value: body.slice(last, m.index) });
    out.push({ type: 'mention', display: m[1], userId: m[2].toLowerCase() });
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push({ type: 'text', value: body.slice(last) });
  return out;
}

/**
 * Build the token a picker inserts when a user is chosen. The display is
 * sanitized so a `]`/`)` in a name can't break the token.
 */
export function buildMentionToken(display, userId) {
  const safe = String(display || '').replace(/[[\]()]/g, ' ').replace(/\s+/g, ' ').trim() || 'user';
  return `@[${safe}](user:${userId})`;
}
